import React, { useMemo, memo } from 'react'
import { PropTypes } from 'prop-types'
import LoadingParent from 'components/LoadingParent'
import {
  useCourseManager,
  programs,
} from 'layers/content/v2/hooks/useCourseManager'

export function defaultProgramResolver(match) {
  const { url } = match || {}
  if (!url) {
    return null
  }

  let urlToTest = url

  if (typeof url === 'string') {
    urlToTest = url.toLowerCase()
  } else {
    return null
  }

  const isTier2 = urlToTest.includes('/tier-2')
  const isMiddleSchool = urlToTest.includes('/middle-school')
  const isHighSchool = urlToTest.includes('/high-school')
  const isElementary = urlToTest.includes('/elementary')

  if (isMiddleSchool) {
    if (isTier2) {
      return programs.middleSchoolTieredInterventions
    }
    return programs.middleSchool
  }

  if (isHighSchool) {
    return programs.highSchool
  }

  if (isElementary) {
    if (isTier2) {
      return programs.elementaryTieredInterventions
    }
    return programs.elementary
  }

  return null
}

export function CourseManagerGetter(Component, options = {}) {
  const {
    programResolver = defaultProgramResolver,
    overrideHsPreferences = {},
  } = options

  // Memoize the CourseManagerGetterComponent to prevent unnecessary re-renders
  const CourseManagerGetterComponent = memo(
    function CourseManagerGetterComponent(props) {
      const { match } = props

      // Memoize the program resolution to prevent unnecessary re-renders
      const program = useMemo(() => programResolver(match, props), [match?.url])

      // Validate program
      if (!program) {
        throw new Error('Program not found')
      }

      // Early return for none program
      if (program === programs.none) {
        // handle the no-op case and just return the component
        // without a course manager instance
        console.log(
          'CourseManagerGetter',
          'no program selected, returning wrapped component without a course manager instance',
        )
        return <Component {...props} courseManager={null} />
      }

      // Use a stable reference for overrideHsPreferences
      const stableOverrideHsPreferences = useMemo(() => {
        return overrideHsPreferences
      }, [JSON.stringify(overrideHsPreferences)])

      // Get course manager with stable references
      const courseManager = useCourseManager({
        program,
        overrideHsPreferences: stableOverrideHsPreferences,
      })

      // Check if there was an error with the course manager
      // This could happen if the user doesn't have access to the program
      if (courseManager?.error) {
        console.warn(
          `[CourseManagerGetter] Error loading course manager: ${courseManager.error.message}`,
          'Returning component without course manager data',
        )
        // Return the component without loading state to prevent error flashes
        return (
          <Component
            {...props}
            courseManager={{ ...courseManager, loading: false }}
          />
        )
      }

      // Show loading screen if either the course manager is loading or the user context is still loading
      if (
        (!!courseManager?.loading || !!courseManager?.isUserContextLoading) &&
        !courseManager?.error
      ) {
        return <LoadingParent loadingText={'Loading user data...'} />
      }

      // Render the component with courseManager prop
      return <Component {...props} courseManager={courseManager} />
    },
  )

  CourseManagerGetterComponent.propTypes = {
    match: PropTypes.shape({
      url: PropTypes.string,
    }),
  }

  // Add displayName for debugging
  CourseManagerGetterComponent.displayName = `CourseManagerGetter(${Component.displayName ||
    Component.name ||
    'Component'})`

  return CourseManagerGetterComponent
}

CourseManagerGetter.displayName = 'CourseManagerGetter'

export const CourseNodeType = PropTypes.shape({
  contentfulId: PropTypes.string,
  contenfulType: PropTypes.string,
  internalTitle: PropTypes.string,
})
CourseNodeType.children = PropTypes.arrayOf(CourseNodeType)

export const CourseManagerType = PropTypes.shape({
  courseTrees: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.object,
  isUserContextLoading: PropTypes.bool,
})
